import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { App } from 'antd';
import { useInboundPackageByIdQuery, useCheckFullPackagesMutation } from 'api/warehouse';
import { WAREHOUSE_ROUTES } from 'constants/routes';

export const usePackaging = () => {
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [packages, setPackages] = useState<any[]>([]);

  const itemInfo = location.state as { itemId: string; sku: string; packages?: any[] } | undefined;

  const {
    data: order,
    isLoading,
    error,
    refetch,
  } = useInboundPackageByIdQuery(id || '', {
    skip: !id,
  });

  const [checkFullPackages] = useCheckFullPackagesMutation();

  useEffect(() => {
    if (error) {
      message.error('Failed to load order details');
      navigate(WAREHOUSE_ROUTES.inboundPackages);
    }
  }, [error, navigate, message]);

  useEffect(() => {
    if (!itemInfo) {
      message.warning('Please identify an item first');
      navigate(`/warehouse/inbound/${id}/capture`);
    }
  }, [itemInfo, id, navigate, message]);

  // Get packages from location state (from set-dimensions API) or order API response
  useEffect(() => {
    // Priority 1: Check if packages came from itemDetails flow (set-dimensions API)
    if (itemInfo?.packages && itemInfo.packages.length > 0) {
      const mappedPackages = itemInfo.packages.map((pkg: any) => ({
        id: pkg.orderId,
        orderNumber: pkg.orderUuid,
        clientName: pkg.clientName,
        orderId: pkg.orderId,
        itemsCount: pkg.quantity,
        clientRequestNumber: pkg.clientRequestNumber,
      }));
      setPackages(mappedPackages);
      return;
    }

    // Priority 2: Get packages from order API response (Edit Item flow)
    if (order?.itemsResponseList && itemInfo?.itemId) {
      const currentItem = order.itemsResponseList.find((item: any) => item.id === itemInfo.itemId);

      if (currentItem?.itemPackagingResponses && currentItem.itemPackagingResponses.length > 0) {
        const mappedPackages = currentItem.itemPackagingResponses.map((pkg: any) => ({
          id: pkg.orderId,
          orderNumber: pkg.orderUuid,
          clientName: pkg.clientName,
          orderId: pkg.orderId,
          itemsCount: pkg.quantity,
          clientRequestNumber: pkg.clientRequestNumber,
        }));
        setPackages(mappedPackages);
      }
    }
  }, [order, itemInfo]);

  const handlePrint = useCallback(
    (packageId: string) => {
      // Find the package details
      const pkg = packages.find((p) => p.id === packageId);
      if (!pkg) {
        message.error('Package not found');
        return;
      }

      // Create a canvas to generate the label image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        message.error('Failed to create label');
        return;
      }

      // Set canvas dimensions (600x400px)
      canvas.width = 600;
      canvas.height = 400;

      // Fill white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw package number
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 48px -apple-system, BlinkMacSystemFont, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const packageText = `PACKAGE - ${pkg.orderNumber}`;
      ctx.fillText(packageText, canvas.width / 2, 140);

      // Draw client name with label
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 32px -apple-system, BlinkMacSystemFont, Arial, sans-serif';
      const clientText = `Client Name - ${pkg.clientName}`;
      ctx.fillText(clientText, canvas.width / 2, 240);

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        if (!blob) {
          message.error('Failed to generate label');
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `package-label-${pkg.orderNumber}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        message.success('Label downloaded successfully');
      }, 'image/png');
    },
    [packages, message]
  );

  const handleDone = useCallback(async () => {
    if (!itemInfo) return;

    try {
      message.loading({ content: 'Checking packages...', key: 'status' });

      // Check if packages are full
      const orderIds = packages.map((pkg) => pkg.orderId);
      const fullPackagesResponse: any = await checkFullPackages({
        orderIds,
        sku: itemInfo.sku,
      });

      if (!fullPackagesResponse?.data?.success) {
        throw new Error('Failed to check package status');
      }

      //TODO: hardcode '68c037c3321634a1ee30afe2'
      console.log(fullPackagesResponse);
      const fullPackageIds = fullPackagesResponse.data.data || [];

      message.success({ content: 'Package check completed', key: 'status' });

      // If no full packages, redirect to order details
      if (fullPackageIds.length === 0) {
        await refetch();
        navigate(`/warehouse/inbound/${id}`);
      } else {
        // Navigate to full package and dimensions page with full package IDs and packages data
        navigate(`/warehouse/inbound/${id}/full-package-dimensions`, {
          state: {
            fullPackageIds,
            itemId: itemInfo.itemId, // Pass itemId for back navigation
            itemSku: itemInfo.sku,
            packages, // Pass the full packages data which includes orderUuid
          },
        });
      }
    } catch (err: any) {
      message.error({
        content: err?.message || 'Failed to check packages',
        key: 'status',
      });
    }
  }, [id, packages, checkFullPackages, navigate, refetch, itemInfo, message]);

  const completedCount =
    order?.itemsResponseList?.filter((item: any) => item.status === 'CHECKED' || item.status === 'CLAIMED').length || 0;

  const handleBack = useCallback(() => {
    // Go back to item details page (previous step)
    navigate(WAREHOUSE_ROUTES.itemDetails.replace(':id', id || ''), {
      state: itemInfo,
    });
  }, [id, navigate, itemInfo]);

  const totalCount = order?.itemsResponseList?.length || 0;

  return {
    isLoading,
    packages,
    handlePrint,
    handleDone,
    handleBack,
    completedCount,
    totalCount,
  };
};
